// Provisioning progress of a repository connected from GitHub (existing-project onboarding, spec §3.1, §3.2): polls the
// project's latest provision every 2 s until it is done or failed and shows cloning, detecting and validating. A failure
// says why in plain words (never the engine's text) and retries the same repository, or (`onReconnect`) opens the
// connect dialog to pick another connection or repository; "done" runs the readiness validation exactly once, shows its
// checks — the D10 execution-policy refusal as the operator step — and calls onDone. A validation that fails shows its
// error; one still running after the polls says so and points to "Validar agora" (never an empty list).
import { useEffect, useRef, useState } from "react";
import { readinessCauseText } from "../blocks/explain.ts";
import { useI18n } from "../../i18n/index.tsx";
import { getProvision, provisionRepository } from "../../api/endpoints-onboarding.ts";
import { validateProject } from "../../api/endpoints.ts";
import type { OperationRecord } from "../../api/types.ts";
import type { RepositoryProvision } from "../../api/types-onboarding.ts";
import { waitForOperation } from "../../pages/project/ExecutionControls.tsx";
import { Button } from "../../components/Button.tsx";
import { Banner, LoadingBlock } from "../../components/Feedback.tsx";
import { ActionError } from "../../components/ActionError.tsx";
import { Pill } from "../../components/Status.tsx";

const STEPS = ["cloning", "detecting", "validating"] as const;
/** Position of each active state along STEPS (1-based); done is past all of them. */
const REACHED: Record<Exclude<RepositoryProvision["state"], "failed">, number> = { queued: 0, cloning: 1, detecting: 2, validating: 3, done: 4 };
const POLL_MS = 2000;
/** The D10 validator's cause when no workspace has opted in to command execution (apps/api/src/services/readiness.ts). */
const OPERATOR_CAUSE = "no command execution opt-in";

type Checks = NonNullable<NonNullable<OperationRecord["result"]>["checks"]>;

export function ProvisionProgress({ projectId, onDone, onReconnect }: { projectId: string; onDone: () => void; onReconnect?: () => void }) {
  const { t } = useI18n();
  const [provision, setProvision] = useState<RepositoryProvision | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [retrying, setRetrying] = useState(false);
  /** null while checking; "failed" when the validation errored (the error is shown); "running" when still unfinished. */
  const [checks, setChecks] = useState<Checks | "failed" | "running" | null>(null);
  const [polls, setPolls] = useState(0); // bumped by a retry to poll again
  const validated = useRef(false);
  const alive = useRef(true);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const validate = async () => {
    if (validated.current) return;
    validated.current = true;
    try {
      const accepted = await validateProject(projectId);
      const op = await waitForOperation(accepted.operation_id, () => alive.current);
      if (!alive.current) return;
      if (op?.status === "accepted" || op?.status === "running") setChecks("running");
      else if (op?.error) {
        setError(new Error(op.error.message));
        setChecks("failed");
      } else setChecks(op?.result?.checks ?? []);
    } catch (e) {
      if (!alive.current) return;
      setError(e);
      setChecks("failed");
    }
    if (alive.current) onDoneRef.current();
  };

  useEffect(() => {
    let live = true;
    let timer: number | undefined;
    const load = async () => {
      try {
        const next = (await getProvision(projectId)).provision;
        if (!live) return;
        setProvision(next);
        setError(null);
        if (next?.state === "done") void validate();
        if (next?.state === "done" || next?.state === "failed") return;
      } catch (e) {
        if (live) setError(e);
      }
      if (live) timer = window.setTimeout(() => void load(), POLL_MS);
    };
    void load();
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, polls]);

  const retry = async () => {
    if (!provision) return;
    setRetrying(true);
    setError(null);
    try {
      const next = await provisionRepository(projectId, { integration_id: provision.integration_id, repository: provision.repository });
      setProvision(next.provision);
      validated.current = false;
      setChecks(null);
      setPolls((n) => n + 1);
    } catch (e) {
      setError(e);
    } finally {
      setRetrying(false);
    }
  };

  if (!provision) return error ? <ActionError error={error} /> : <LoadingBlock label={t("common.loading")} lines={2} />;

  return (
    <div className="stack" data-testid="provision-progress" data-state={provision.state} aria-live="polite">
      {provision.state === "failed" ? (
        <Banner
          tone="error"
          role="alert"
          title={t("githubOnboarding.failed")}
          action={
            <div className="row row--wrap">
              <Button size="sm" onClick={() => void retry()} loading={retrying}>
                {t("githubOnboarding.retry")}
              </Button>
              {onReconnect ? (
                <Button size="sm" onClick={onReconnect} disabled={retrying}>
                  {t("githubOnboarding.connectTitle")}
                </Button>
              ) : null}
            </div>
          }
        >
          {t(`githubOnboarding.error.${provision.error_code ?? "clone_failed"}`)}
        </Banner>
      ) : (
        <ol className="plain-list">
          {STEPS.map((s, i) => {
            const reached = REACHED[provision.state as keyof typeof REACHED];
            const state = reached > i + 1 ? "done" : reached === i + 1 ? "current" : "pending";
            return (
              <li key={s} data-testid="provision-step" data-state={state}>
                <Pill tone={state === "done" ? "success" : state === "current" ? "info" : "neutral"} icon={state === "done" ? "check" : state === "current" ? "spinner" : "circle"}>
                  {t(`githubOnboarding.step.${s}`)}
                  <span className="sr-only"> — {t(`githubOnboarding.stepState.${state}`)}</span>
                </Pill>
              </li>
            );
          })}
        </ol>
      )}
      {provision.state === "done" ? (
        <>
          <p>{provision.test_command ? t("githubOnboarding.testCommand", { command: provision.test_command }) : t("githubOnboarding.noTestCommand")}</p>
          {checks === null ? (
            <p className="muted">{t("githubOnboarding.checkingReadiness")}</p>
          ) : checks === "running" ? (
            <p data-testid="provision-readiness-running">{t("githubOnboarding.stillChecking")}</p>
          ) : checks === "failed" ? null : (
            <ul className="plain-list" data-testid="provision-readiness">
              {checks.map((c) => (
                <li key={c.check} className="row row--wrap" data-check={c.check} data-status={c.status}>
                  <Pill tone={c.status === "passed" ? "success" : c.status === "failed" ? "danger" : "neutral"} icon={c.status === "passed" ? "check" : c.status === "failed" ? "x" : "circle"}>
                    {t(`readiness.check.${c.check}` as "readiness.check.repository")}
                  </Pill>
                  {c.check === "executor_available" && c.cause?.includes(OPERATOR_CAUSE) ? (
                    <span>{t("githubOnboarding.operatorPending")}</span>
                  ) : c.cause ? (
                    <span className="subtle user-content">{readinessCauseText(t, c.cause)}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </>
      ) : null}
      {error ? <ActionError error={error} /> : null}
    </div>
  );
}
